import { createContext, useContext } from 'react';
import { noop } from 'lodash';
import { RoundedModalProps } from './rounded-modal/rounded-modal';

export type ModalControlContextType = {
  /** Whether the modal is currently open */
  isOpen: RoundedModalProps['isOpen'];
  /** Function to handle modal open/close state changes */
  onOpenChange: RoundedModalProps['onOpenChange'];
  /** Function to close the modal through the regular onOpenChange flow */
  close: () => void;
  /** Function to force close the modal bypassing onOpenChange logic.
   * Usefull for example when you need to close the modal at the end of some flow (like long form) and bypass the regular onOpenChange logic where you might have some analytics of "are you sure you want to close?" etc. */
  forceClose?: (() => void) | undefined;
};

/**
 * Modal Control Context
 *
 * This context is to pass the modal control function to inside the modal.
 * The modal can be closed using multiple ways (button, clicking outside, esc key)
 * so you need to pass the close function only once, and have it available everywhere within the modal tree.
 * Sometimes you need to add some analytics, or another "are you sure you want to close" check so you can use this context to do that
 */
export const ModalControlContext = createContext<ModalControlContextType>({
  isOpen: false,
  onOpenChange: undefined,
  close: noop,
  forceClose: undefined,
});

export const useModalControlContext = () => {
  const context = useContext(ModalControlContext);
  if (!context) {
    throw new Error('useModalControlContext must be used within a ModalControlProvider');
  }
  return context;
};
